import React from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import useTwilio, { Participant as ParticipantType } from '../useTwilio'
import RouteLeavingPrompt from '../../../components/RouteLeavingPrompt'
import BottomBar from '../components/BottomBar'
import { _ } from '../../../utils/localization'
import DefaultBottomBarContent from '../components/DefaultBottomBarContent'
import {
    selectSpeaker,
    selectIsScreenSharingEnabled,
    selectSharingScreenId,
    selectLayout,
    selectIsSpeaker,
} from '../videocallSlice'
import SharedScreen from '../components/SharedScreen'
import MyScreen from '../components/MyScreen'
import ParticipantsLayout from '../components/ParticipantsLayout'
import SmallParticipantsLayout from '../components/SmallParticipantsLayout'
import FullPageLoading from './FullPageLoading'
import Participant from '../components/Participant'
import LayoutSelect from '../components/LayoutSelect'
import {
    selectGotKicked,
    selectIsMeeting,
    selectIsMeetingHost,
    selectMeetingHost,
} from '../../meetings/currentMeetingSlice'

const S = {
    LayoutSelectWrapper: styled.div`
      height: 50px;
      margin-bottom: -50px;
      margin-left: auto;
      padding: 12px;
      z-index: 99;
    `,
    LayoutWrapper: styled.div`
      flex-grow: 1;
      background: lightgray;
      display: flex;
      flex-wrap: wrap;
      height: 100%;
      width: 100%;
      overflow: hidden;
    `,
    Room: styled.div`
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      overflow: hidden;
    `,
    LocalParticipantWrapper: styled.div`
      display: flex;
      height: 100%;
      width: 300px;
      border-radius: 16px;
      margin-right: 32px;
      overflow: hidden;
    `,
}

type EnlargeParticipantLayoutProps = { participants: ParticipantType[], enlarged: string }
const EnlargeParticipantLayout = ({ participants, enlarged }: EnlargeParticipantLayoutProps) => {
    const enlargedParticipant = (
        participants.find((p) => p.identity === enlarged)
    )
    const otherParticipants = participants.filter((p) => p.identity !== enlarged)
    return (
        <>
            {
                enlargedParticipant
                    ? <Participant participant={enlargedParticipant} />
                    : <FullPageLoading text={_('videocall.participant_not_available')} />
            }
            <SmallParticipantsLayout participants={otherParticipants} />
        </>
    )
}

type EnlargeLocalParticipantLayoutProps = {
    participants: ParticipantType[],
    localParticipant: ParticipantType
}
const EnlargeLocalParticipantLayout = ({
    participants,
    localParticipant,
}: EnlargeLocalParticipantLayoutProps) => (
    <>
        <Participant participant={localParticipant} />
        <SmallParticipantsLayout participants={participants} />
    </>
)

type LayoutProps = { participants: ParticipantType[], localParticipant: ParticipantType }
const Layout = ({ participants, localParticipant }: LayoutProps) => {
    const layout = useSelector(selectLayout)
    const speaker = useSelector(selectSpeaker)
    const host = useSelector(selectMeetingHost)
    const currentUserIsHost = useSelector(selectIsMeetingHost)
    const currentUserIsSpeaker = useSelector(selectIsSpeaker)
    const isSharingMyScreen = useSelector(selectIsScreenSharingEnabled)
    const sharingScreenId = useSelector(selectSharingScreenId)
    const sharingScreenParticipant = sharingScreenId && (
        participants.find((p) => p.identity === sharingScreenId)
    )

    if (isSharingMyScreen) {
        return (
            <>
                <MyScreen />
                <SmallParticipantsLayout participants={participants} />
            </>
        )
    }

    if (sharingScreenParticipant) {
        return (
            <>
                <SharedScreen participant={sharingScreenParticipant} />
                <SmallParticipantsLayout participants={participants} />
            </>
        )
    }

    if (participants.length === 0) {
        return <FullPageLoading text={_('videocall.wait_for_participants')} />
    }

    if (
        (layout === 'speaker' && currentUserIsSpeaker)
        || (layout === 'host' && currentUserIsHost)
    ) {
        return (
            <EnlargeLocalParticipantLayout
                participants={participants}
                localParticipant={localParticipant}
            />
        )
    }

    if (speaker && layout === 'speaker') {
        return <EnlargeParticipantLayout participants={participants} enlarged={`${speaker}`} />
    }

    if (host && layout === 'host') {
        return <EnlargeParticipantLayout participants={participants} enlarged={`${host}`} />
    }

    return <ParticipantsLayout participants={participants} />
}

const VideoCall = ({ bottomBarContent }: { bottomBarContent?: JSX.Element }) => {
    const twilio = useTwilio()
    const isMeeting = useSelector(selectIsMeeting)
    const gotKicked = useSelector(selectGotKicked)

    if (!twilio.isSupported) {
        return <p>Browser is not supported</p>
    }

    if (twilio.failedToJoin) {
        return <p>Failed to join room</p>
    }

    return (
        <S.Room>
            {isMeeting
            && (
                <S.LayoutSelectWrapper>
                    <LayoutSelect />
                </S.LayoutSelectWrapper>
            )}
            {/* todo  put `env.disableRouteLeavePromt` back */}
            { !gotKicked && (
                <RouteLeavingPrompt
                    when
                    confirmText={_('videocall.confirm_leave_question')}
                    okButton={_('videocall.confirm_leave_button_yes')}
                    cancelButton={_('videocall.confirm_leave_button_no')}
                />
            )}

            <S.LayoutWrapper>
                <Layout
                    localParticipant={twilio.localParticipant}
                    participants={twilio.participants}
                />
            </S.LayoutWrapper>

            <BottomBar>
                <S.LocalParticipantWrapper>
                    { twilio.localParticipant && (
                        <Participant participant={twilio.localParticipant} />
                    )}
                </S.LocalParticipantWrapper>
                { bottomBarContent || (
                    <DefaultBottomBarContent />
                ) }
            </BottomBar>
        </S.Room>
    )
}

export default VideoCall
